import { useEffect, useState } from "react";
import { Ticket, Copy, Plus } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { api, errMsg } from "@/lib/api";

const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "never");

export const PromoCodes = () => {
  const [codes, setCodes] = useState(null);
  const [f, setF] = useState({ code: "", days: 60, max_uses: "", expires_at: "", note: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/admin/promo-codes").then((r) => setCodes(r.data)).catch((e) => toast.error(errMsg(e))); }, []);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/admin/promo-codes", { ...f, days: Number(f.days) || 60, max_uses: f.max_uses ? Number(f.max_uses) : null,
        expires_at: f.expires_at ? new Date(f.expires_at + "T23:59:59").toISOString() : null });
      setCodes((l) => [data, ...l]);
      setF({ code: "", days: 60, max_uses: "", expires_at: "", note: "" });
      toast.success(`Code ${data.code} created`);
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  const toggle = async (p) => {
    try { const { data } = await api.patch(`/admin/promo-codes/${p.id}/toggle`); setCodes((l) => l.map((x) => (x.id === p.id ? data : x))); }
    catch (err) { toast.error(errMsg(err)); }
  };

  return (
    <div className="space-y-4" data-testid="promo-codes-section">
      <div>
        <h2 className="text-lg font-semibold flex items-center gap-2"><Ticket className="h-5 w-5 text-amber-500" /> Promo codes</h2>
        <p className="text-sm text-muted-foreground">Share a code; the business enters it on their Billing page and gets free days instantly.</p>
      </div>
      <form onSubmit={create} className="surface p-4 grid sm:grid-cols-[1fr_90px_90px_150px_1fr_auto] gap-3 items-end">
        <div className="space-y-1"><Label>Code</Label><Input data-testid="promo-code-input" required value={f.code} onChange={(e) => setF({ ...f, code: e.target.value.toUpperCase() })} placeholder="LAWN60" className="font-mono uppercase" /></div>
        <div className="space-y-1"><Label>Days</Label><Input data-testid="promo-days-input" type="number" min="1" max="365" value={f.days} onChange={set("days")} className="font-mono" /></div>
        <div className="space-y-1"><Label>Max uses</Label><Input data-testid="promo-max-uses-input" type="number" min="1" value={f.max_uses} onChange={set("max_uses")} placeholder="∞" className="font-mono" /></div>
        <div className="space-y-1"><Label>Expires</Label><Input data-testid="promo-expires-input" type="date" value={f.expires_at} onChange={set("expires_at")} /></div>
        <div className="space-y-1"><Label>Note</Label><Input data-testid="promo-note-input" value={f.note} onChange={set("note")} placeholder="Trade show flyer" /></div>
        <Button data-testid="promo-create-button" type="submit" disabled={busy} className="h-10 bg-emerald-600 hover:bg-emerald-700 text-white font-bold"><Plus className="h-4 w-4 mr-1" /> Create</Button>
      </form>
      {codes && codes.length > 0 && (
        <div className="surface overflow-x-auto">
          <table className="w-full text-sm" data-testid="promo-codes-table">
            <thead><tr className="border-b border-border text-xs font-bold uppercase tracking-wider text-muted-foreground text-left">
              <th className="px-4 py-2">Code</th><th className="px-4 py-2 text-right">Days</th><th className="px-4 py-2 text-right">Used</th><th className="px-4 py-2">Expires</th><th className="px-4 py-2">Note</th><th className="px-4 py-2">Status</th></tr></thead>
            <tbody>
              {codes.map((p) => (
                <tr key={p.id} data-testid={`promo-row-${p.code}`} className={`border-b border-border last:border-0 ${p.active ? "" : "opacity-50"}`}>
                  <td className="px-4 py-2"><button type="button" onClick={() => navigator.clipboard?.writeText(p.code).then(() => toast.success("Copied"))} className="font-mono font-bold inline-flex items-center gap-1 hover:text-emerald-600">{p.code} <Copy className="h-3 w-3" /></button></td>
                  <td className="px-4 py-2 text-right font-mono">{p.days}</td>
                  <td className="px-4 py-2 text-right font-mono" data-testid={`promo-uses-${p.code}`}>{p.uses}{p.max_uses ? ` / ${p.max_uses}` : ""}</td>
                  <td className="px-4 py-2 text-xs">{fmt(p.expires_at)}</td>
                  <td className="px-4 py-2 text-xs text-muted-foreground">{p.note}</td>
                  <td className="px-4 py-2"><button type="button" data-testid={`promo-toggle-${p.code}`} onClick={() => toggle(p)} className={`text-xs font-bold ${p.active ? "text-emerald-600" : "text-red-600"}`}>{p.active ? "Active · disable" : "Disabled · enable"}</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
